export const OPERATION_TYPE_LABELS = {
  transfer_out: "Transfer out",
  transfer_in: "Transfer in",
  fee: "Bank fee",
  vat: "VAT",
  withdrawal: "Withdrawal",
  direct_debit: "Direct debit",
  card: "Card payment",
  tax: "Tax",
  salary: "Salary",
  dividend: "Dividend",
  subscription: "Subscription",
  other: "Other",
};

export const PARTY_TYPES = {
  provider: { label: "Provider", plural: "Providers", tone: "provider" },
  artist: { label: "Artist", plural: "Artists", tone: "artist" },
  label: { label: "Label", plural: "Labels", tone: "label" },
  contributor: { label: "Contributor", plural: "Contributors", tone: "contributor" },
  staff: { label: "Staff", plural: "Staff", tone: "staff" },
  organization: { label: "Organization", plural: "Organizations", tone: "organization" },
  other: { label: "Other", plural: "Other", tone: "other" },
  unclassified: { label: "Unclassified", plural: "Unclassified", tone: "unclassified" },
};

export const partyTypeLabel = (type) => PARTY_TYPES[type || "unclassified"]?.label || type;

export const STATUS_META = {
  uploaded: { label: "Queued", color: "secondary" },
  parsing: { label: "Parsing", color: "info" },
  parsed: { label: "Needs linking", color: "warning" },
  reconciled: { label: "Reconciled", color: "success" },
  mismatch: { label: "Mismatch", color: "danger" },
  failed: { label: "Failed", color: "danger" },
};

export const PERMISSIONS = {
  VIEW: "gl.view",
  UPLOAD: "gl.upload",
  ANNOTATE: "gl.annotate",
  ATTACH: "gl.attach",
  EXPORT: "gl.export",
  MANAGE_PROFILES: "gl.manage_profiles",
  DELETE: "gl.delete",
};

export const can = (meta, permission) => Boolean(meta?.permissions?.[permission]);

export const operationLabel = (type) => OPERATION_TYPE_LABELS[type] || type || "—";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "2026-09-28" -> "28 Sep 2026" (or "28 Sep" without the year); parsed by hand so no timezone shift applies. */
export const formatShortDate = (iso, withYear = true) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || "");
  if (!match) return iso || "—";
  const [, year, month, day] = match;
  const label = `${day} ${MONTHS[Number(month) - 1]}`;
  return withYear ? `${label} ${year}` : label;
};

export const formatSize = (bytes) => {
  if (!bytes && bytes !== 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const filenameFromDisposition = (disposition, fallback) => {
  if (!disposition) return fallback;
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (utf8) return decodeURIComponent(utf8[1]);
  const plain = /filename="?([^";]+)"?/i.exec(disposition);
  return plain ? plain[1] : fallback;
};

/** Saves an axios blob response to disk without exposing any file URL. */
export const saveBlobResponse = (response, fallbackName) => {
  const name = filenameFromDisposition(response?.headers?.["content-disposition"], fallbackName);
  const url = window.URL.createObjectURL(response.data);
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
};

/** Blob error bodies (responseType: blob) need to be decoded to read the JSON message. */
export const readBlobError = async (error) => {
  const blob = error?.response?.data;
  if (blob instanceof Blob) {
    try {
      const parsed = JSON.parse(await blob.text());
      return parsed?.message || null;
    } catch {
      return null;
    }
  }
  return null;
};
